import i18n from "@/Plugins/i18n";

import * as Sentry from "@sentry/vue";

import { VIDEO_CALL_COMPLETE_REASON } from "@/Modules/Models/Api/VideoCalls";
import { VIDEO_CALL_EXCEPTIONS } from "../Components/definitions";

import useChatStore from "@/Modules/Chat/Store";
import useVideoCallStore from "../Store";
import useAuthStore from "@/Modules/Auth/Store";
import useAppStore from "@/Modules/App/Store";

import { ApiErrorCodes } from "@/Modules/App/Models/Api/ApiErrorCodes";
import { getPatientRoomsInProgress } from "@/Services/TherapyApi/VideoCalls";
import { isApiError } from "@/Services/Api/Typeguards/isApiError";

import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";
import { log } from "@/Utils/Debug";
import { not } from "@/Utils/Promises";

import { hasPermissions, hasSupport } from "../Utils";

const checkVideoCallInProgressActionHandler = async (chatId?: string): Promise<boolean> => {
  try {
    const chatStore = useChatStore();
    const authStore = useAuthStore();
    const appStore = useAppStore();
    const videoCallStore = useVideoCallStore();

    const rooms = await getPatientRoomsInProgress().catch((e) => {
      if (isApiError(e)) {
        if (e.error_code === ApiErrorCodes.USER_LOGED_OUT) {
          authStore.logout();
        }
      }

      return [];
    });

    if (rooms.length === 0) {
      if (isValidString(videoCallStore.roomId)) {
        await videoCallStore.hangDownVideoCall(videoCallStore.roomId, VIDEO_CALL_COMPLETE_REASON.OK);
      }
    } else {
      const [room] = rooms;

      if (
        !isValidString(room.room_uuid) ||
        !isValidString(room.chat_id) ||
        !isValidString(room.token) ||
        !isValidString(room.session_id)
      ) {
        Sentry.captureMessage("Invalid room config for push" + JSON.stringify(room));
      } else {
        const chat = chatStore.rooms[room.chat_id.replaceAll("-", "")];

        if (!hasValue(chat)) {
          log(`Invalid chat id -- ${chatId} -- ${room.room_uuid}`);
        } else {
          log("Init video_call", chat.user, JSON.stringify(room));

          if (chatStore.activeChatRoomId !== chat.id) {
            chatStore.setActiveChatRoom(chat.id);
          }

          if (videoCallStore.roomId === room.room_uuid) return true;
          if (isValidString(videoCallStore.roomId)) {
            // Rooms diferentes colgar la vieja
            await videoCallStore.hangDownVideoCall(videoCallStore.roomId, VIDEO_CALL_COMPLETE_REASON.OK);
          }

          if (!hasSupport() || (await not(hasPermissions()))) {
            log("NOT PERMISSIONS");

            videoCallStore.updateVideoCallError(true);
            videoCallStore.addError(VIDEO_CALL_EXCEPTIONS.NOT_PERMISSIONS);

            await videoCallStore.sendVideoCallLogs(room.room_uuid);
            await videoCallStore.hangDownVideoCall(room.room_uuid, VIDEO_CALL_COMPLETE_REASON.PERMISSIONS);

            return false;
          }

          const user = chatStore.rooms[chat.id].user;
          const videoCallConfig = {
            roomId: room.room_uuid,
            sessionId: room.session_id,
            token: room.token,
            isTranscribed: room.is_transcription_enabled,
            remoteUser: chat.user,
            isRatingRequired: room.is_rating_required,
          };

          videoCallStore.disableRating();

          if (await videoCallStore.isValidConfig()) {
            videoCallStore.showVideoCallError = false;

            // Tener en cuenta que si el paciente en algún momento tiene mas de un chat esto se verá modificado

            chatStore.setActiveChatRoom(chatId);
            videoCallStore.setupVideoCall(videoCallConfig);

            appStore.notificate({
              title: user.username,
              body: i18n.global.t("browser-notifications.new_videocall"),
            });
          } else {
            chatStore.setActiveChatRoom(chatId);

            videoCallStore.updateShowConfig(true);
            videoCallStore.updateNextCallOptions(videoCallConfig);
          }

          return true;
        }
      }
    }

    return false;
  } catch (e) {
    Sentry.captureException(e);

    return false;
  }
};

export default checkVideoCallInProgressActionHandler;
